import React, { useState, useEffect, useRef } from "react";
import { Microphone, Waveform, Brain, Quotes, Target, Warning } from "@phosphor-icons/react";
import { FileText, ListChecks, ScanFace, MessageSquareQuote, Tags, ShieldAlert } from "lucide-react";
import '../../styles/navbar/SpeechAnalysis.css';
import { useMamdani } from '../../context/MamdaniContext';

const API_BASE = process.env.REACT_APP_API_URL || "https://api.anti-bot.ai";

// ─── Typewriter Effect ───────────────────────────────────────────────────────
function useTypewriter(text, speed = 18, active = false) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active || !text) return;
    setDisplayed("");
    setDone(false);
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(iv);
        setDone(true);
      }
    }, speed);
    return () => clearInterval(iv);
  }, [text, active]);

  return { displayed, done };
}

// ─── Audio Wave Visual ───────────────────────────────────────────────────────
function AudioWave({ active }) {
  return (
    <div className="sa-wave-container">
      {Array.from({ length: 28 }).map((_, i) => (
        <div
          key={i}
          className={`sa-wave-bar ${active ? "sa-wave-bar--active" : ""}`}
          style={{ animationDelay: `${(i * 0.08) % 1.2}s` }}
        />
      ))}
    </div>
  );
}

// ─── Step Badge ──────────────────────────────────────────────────────────────
function StepBadge({ num, label, status }) {
  return (
    <div className={`sa-step-badge sa-step-badge--${status}`}>
      <span className="sa-step-num">{status === "done" ? "✓" : num}</span>
      <span className="sa-step-label">{label}</span>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
const MAMDANI_SPEAKERS = [
  { key: "mamdani",   label: "Zohran Mamdani",    party: "NYC Mayor" },
  { key: "sarsour",   label: "Linda Sarsour",     party: "Activist" },
  { key: "brisport",  label: "Jabari Brisport",   party: "NY State Senate" },
  { key: "caban",     label: "Tiffany Cabán",     party: "NYC Council" },
  { key: "munayyer",  label: "Yousef Munayyer",   party: "Political Analyst" },
  { key: "kiswani",   label: "Nerdeen Kiswani",   party: "Activist" },
  { key: "nycdsa",    label: "NYC DSA",            party: "Org." },
];

export default function SpeechAnalysis() {
  const { mamdaniMode: mm } = useMamdani();
  const [speaker, setSpeaker] = useState("lapid");
  const [phase, setPhase] = useState("idle");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const transcriptRef = useRef(null);

  const SPEAKERS = mm ? MAMDANI_SPEAKERS : [
    { key: "netanyahu", label: "בנימין נתניהו",   party: "הליכוד" },
    { key: "liberman",  label: "אביגדור ליברמן", party: "ישראל ביתנו" },
    { key: "bengvir",   label: "איתמר בן גביר",   party: "עוצמה יהודית" },
    { key: "bennett",   label: "נפתלי בנט",        party: "ביחד" },
    { key: "smotrich",  label: "בצלאל סמוטריץ'",  party: "הציונות הדתית" },
    { key: "eisenkot",  label: "גדי איזנקוט",      party: "ישר" },
    { key: "gantz",     label: "בני גנץ",          party: "כחול לבן" },
    { key: "lapid",     label: "יאיר לפיד",        party: "ביחד" },
    { key: "golan",     label: "יאיר גולן",        party: "הדמוקרטים" },
    { key: "levin",     label: "יריב לוין",        party: "הליכוד" },
    { key: "kisch",     label: "יואב קיש",         party: "הליכוד" },
  ];

  // כשמשתנה מוד — איפוס הדובר
  useEffect(() => {
    setSpeaker(mm ? "mamdani" : "lapid");
    setPhase("idle");
    setData(null);
  }, [mm]);

  const handleAnalyze = async () => {
    setError(null);
    setData(null);
    setPhase("loading");

    try {
      const res = await fetch(`${API_BASE}/api/speech/latest/${speaker}`);
      const json = await res.json();

      if (!json.success) throw new Error(json.error || "שגיאה בשרת");

      setData(json);

      setTimeout(() => setPhase("audio"), 600);
      setTimeout(() => setPhase("transcript"), 2200);
      setTimeout(() => setPhase("analysis"), 2200 + json.transcript.length * 18 + 800);

    } catch (e) {
      setError(e.message);
      setPhase("idle");
    }
  };

  useEffect(() => {
    if (phase === "transcript" && transcriptRef.current) {
      transcriptRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const analysis = data?.analysis || {};
  const summary = analysis.summary || {};
  const rhetoric = analysis.rhetoric || {};
  const themes = analysis.themes || [];
  const soundbites = analysis.soundbites || [];
  const riskFlags = analysis.risk_flags || [];
  const namedEntities = analysis.named_entities || {};

  const tw = useTypewriter(data?.transcript || "", 12, phase === "transcript" || phase === "analysis");

  const stepStatus = (key) => {
    const order = ["audio", "transcript", "analysis"];
    const phaseIdx = order.indexOf(phase);
    const keyIdx = order.indexOf(key);
    if (phaseIdx > keyIdx) return "done";
    if (phaseIdx === keyIdx) return "active";
    return "pending";
  };

  const t = mm ? {
    title:    "Speech Analysis AI",
    subtitle: "Real-time · NYC City Hall",
    source:   "Source: NYC City Hall · Public Statements",
    select:   "Select Speaker:",
    analyze:  "Analyze Speech",
    loading:  "Loading...",
    processing: "Processing...",
    capTitle: "System Capabilities",
    capHint:  'Select a speaker and click "Analyze Speech" for a full AI analysis',
    caps: [
      { Icon: FileText,           label: 'AI Summary',       color: '#8B5CF6', desc: 'Smart speech summary' },
      { Icon: ListChecks,         label: 'Key Points',       color: '#3B82F6', desc: 'Most important points' },
      { Icon: ScanFace,           label: 'Tone & Emotions',  color: '#EC4899', desc: 'Tone and emotion detection' },
      { Icon: MessageSquareQuote, label: 'Strong Quotes',    color: '#06B6D4', desc: 'Notable soundbites' },
      { Icon: Tags,               label: 'Keywords',         color: '#22C55E', desc: 'Key words and concepts' },
      { Icon: ShieldAlert,        label: 'Risk Flags',       color: '#F59E0B', desc: 'Sensitive content detection' },
    ],
    steps:   ["Audio", "Transcript", "Analysis"],
    newAnalysis: "New Analysis",
  } : {
    title:    "ניתוח נאומים AI",
    subtitle: "בזמן אמת · ערוץ הכנסת",
    source:   "מקור שידור: ערוץ הכנסת",
    select:   "בחר דובר/ת:",
    analyze:  "נתח נאום",
    loading:  "טוען...",
    processing: "מעבד...",
    capTitle: "יכולות המערכת",
    capHint:  'בחר דובר/ת ולחץ על "נתח נאום" כדי לקבל ניתוח AI מלא ומעמיק',
    caps: [
      { Icon: FileText,           label: 'סיכום AI',        color: '#8B5CF6', desc: 'תקציר חכם של הנאום' },
      { Icon: ListChecks,         label: 'נקודות מפתח',     color: '#3B82F6', desc: 'הנקודות החשובות ביותר' },
      { Icon: ScanFace,           label: 'טון ורגשות',      color: '#EC4899', desc: 'גילוי טון ואמוציות' },
      { Icon: MessageSquareQuote, label: 'ציטוטים חזקים',  color: '#06B6D4', desc: 'הסאונד-בייטס הבולטים' },
      { Icon: Tags,               label: 'מילות מפתח',      color: '#22C55E', desc: 'מילים ומושגים מרכזיים' },
      { Icon: ShieldAlert,        label: 'דגלי סיכון',      color: '#F59E0B', desc: 'איתור תכנים רגישים' },
    ],
    steps:   ["אודיו", "תמלול", "ניתוח"],
    newAnalysis: "ניתוח חדש",
  };

  const steps = [
    { num: 1, label: t.steps[0], key: "audio" },
    { num: 2, label: t.steps[1], key: "transcript" },
    { num: 3, label: t.steps[2], key: "analysis" },
  ];

  return (
    <div className="sa-root" dir={mm ? "ltr" : "rtl"}>

      {/* ── Header ── */}
      <div className="sa-header">
        <div className="sa-header-icon">
          <Microphone size={28} weight="duotone" />
        </div>
        <div>
          <h1 className="sa-title">{t.title}</h1>
          <p className="sa-subtitle">{t.subtitle}</p>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="sa-controls">
        <div className="sa-source-badge">
          <Waveform size={14} weight="duotone" />
          <span>{t.source}</span>
        </div>

        <div className="sa-speaker-row">
          <span className="sa-label">{t.select}</span>
          <div className="sa-speaker-list">
            {SPEAKERS.map(s => (
              <button
                key={s.key}
                className={`sa-speaker-btn ${speaker === s.key ? "sa-speaker-btn--active" : ""}`}
                onClick={() => setSpeaker(s.key)}
                disabled={phase !== "idle"}
              >
                <span className="sa-speaker-name">{s.label}</span>
                <span className="sa-speaker-party">{s.party}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          className={`sa-analyze-btn ${phase !== "idle" ? "sa-analyze-btn--loading" : ""}`}
          onClick={handleAnalyze}
          disabled={phase !== "idle" && phase !== "analysis"}
        >
          {phase === "loading" ? (
            <><span className="sa-spinner" /> {t.loading}</>
          ) : phase !== "idle" && phase !== "analysis" ? (
            <><span className="sa-spinner" /> {t.processing}</>
          ) : (
            <><Brain size={18} weight="duotone" /> {t.analyze}</>
          )}
        </button>
      </div>

      {/* ── System Capabilities ── */}
      {phase === "idle" && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ color: '#fff', fontSize: '16px', fontWeight: 700 }}>{t.capTitle}</span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>{t.capHint}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {t.caps.map(({ Icon, label, color, desc }) => (
              <div key={label} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '14px',
                padding: '16px',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* אייקון + כותרת */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: `${color}18`,
                    border: `1px solid ${color}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={16} color={color} />
                  </div>
                  <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>{label}</span>
                </div>
                {/* שורות placeholder */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', filter: 'blur(3px)', opacity: 0.25 }}>
                  <div style={{ height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.3)', width: '85%' }} />
                  <div style={{ height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.2)', width: '65%' }} />
                  <div style={{ height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.15)', width: '75%' }} />
                </div>
                {/* מנעול */}
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '20px', opacity: 0.3 }}>🔒</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="sa-error">
          <Warning size={18} weight="duotone" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Progress Steps ── */}
      {phase !== "idle" && (
        <div className="sa-steps">
          {steps.map(s => (
            <StepBadge key={s.key} num={s.num} label={s.label} status={stepStatus(s.key)} />
          ))}
        </div>
      )}

      {/* ── Phase: Audio ── */}
      {(phase === "audio" || phase === "transcript" || phase === "analysis") && (
        <div className="sa-card sa-audio-card">
          <div className="sa-card-header">
            <Waveform size={18} weight="duotone" />
            <span>קובץ אודיו</span>
            <span className="sa-badge sa-badge--green">WAV · 16kHz Mono</span>
          </div>
          <AudioWave active={phase === "audio"} />
          <div className="sa-audio-meta">
            <span>🎬 {data?.title}</span>
            <span>📅 {data?.published_at ? new Date(data.published_at).toLocaleDateString("he-IL") : ""}</span>
            <a href={data?.url} target="_blank" rel="noopener noreferrer" className="sa-link">
              {mm ? "Watch ↗" : "צפה בסרטון ↗"}
            </a>
          </div>
          <audio
  controls
  className="sa-audio-player"
  src={`${API_BASE}/api/speech/audio/${speaker}`}
/>
        </div>
      )}

      {/* ── Phase: Transcript ── */}
      {(phase === "transcript" || phase === "analysis") && (
        <div className="sa-card sa-transcript-card" ref={transcriptRef}>
          <div className="sa-card-header">
            <Microphone size={18} weight="duotone" />
            <span>{mm ? "Transcript" : "תמלול"}</span>
            {tw.done && <span className="sa-badge sa-badge--blue">{mm ? "Complete" : "הושלם"}</span>}
          </div>
          <p className="sa-transcript-text">
            {tw.displayed}
            {!tw.done && <span className="sa-cursor">|</span>}
          </p>
        </div>
      )}

      {/* ── Phase: Analysis ── */}
      {phase === "analysis" && tw.done && (
        <div className="sa-analysis-section">

          {summary.one_liner && (
            <div className="sa-card sa-oneliner-card">
              <div className="sa-card-header">
                <Brain size={18} weight="duotone" />
                <span>{mm ? "Summary" : "תקציר"}</span>
              </div>
              <p className="sa-oneliner">{summary.one_liner}</p>
              {summary.short && <p className="sa-short">{summary.short}</p>}
              {summary.key_points?.length > 0 && (
                <ul className="sa-keypoints">
                  {summary.key_points.map((pt, i) => <li key={i}>{pt}</li>)}
                </ul>
              )}
            </div>
          )}

          {rhetoric.tone && (
            <div className="sa-card sa-rhetoric-card">
              <div className="sa-card-header">
                <Target size={18} weight="duotone" />
                <span>{mm ? "Rhetoric" : "רטוריקה"}</span>
              </div>
              <div className="sa-rhetoric-grid">
                <div className="sa-rhetoric-item">
                  <span className="sa-rhetoric-label">{mm ? "Tone" : "טון"}</span>
                  <span className="sa-rhetoric-val">{rhetoric.tone}</span>
                </div>
                {rhetoric.emotions?.length > 0 && (
                  <div className="sa-rhetoric-item">
                    <span className="sa-rhetoric-label">{mm ? "Emotions" : "רגשות"}</span>
                    <div className="sa-tags">
                      {rhetoric.emotions.map((e, i) => <span key={i} className="sa-tag">{e}</span>)}
                    </div>
                  </div>
                )}
                {rhetoric.persuasion_techniques?.length > 0 && (
                  <div className="sa-rhetoric-item">
                    <span className="sa-rhetoric-label">{mm ? "Techniques" : "טכניקות"}</span>
                    <div className="sa-tags">
                      {rhetoric.persuasion_techniques.map((t, i) => <span key={i} className="sa-tag">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {themes.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-header">
                <Brain size={18} weight="duotone" />
                <span>{mm ? "Key Themes" : "נושאים מרכזיים"}</span>
              </div>
              <div className="sa-themes">
                {themes.map((t, i) => (
                  <div key={i} className="sa-theme-item">
                    <div className="sa-theme-name">{t.theme}</div>
                    <div className="sa-theme-why">{t.why_it_matters}</div>
                    {t.evidence_quotes?.slice(0, 2).map((q, j) => (
                      <blockquote key={j} className="sa-quote">❝{q}❞</blockquote>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {soundbites.length > 0 && (
            <div className="sa-card sa-soundbites-card">
              <div className="sa-card-header">
                <Quotes size={18} weight="duotone" />
                <span>{mm ? "Strong Soundbites" : "סאונד-בייטס חזקים"}</span>
              </div>
              <div className="sa-soundbites">
                {soundbites.slice(0, 3).map((sb, i) => (
                  <div key={i} className="sa-soundbite">
                    <blockquote className="sa-soundbite-quote">❝{sb.quote}❞</blockquote>
                    <p className="sa-soundbite-why">→ {sb.why_strong}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.values(namedEntities).some(v => v?.length > 0) && (
            <div className="sa-card">
              <div className="sa-card-header">
                <Target size={18} weight="duotone" />
                <span>{mm ? "Named Entities" : "ישויות מוזכרות"}</span>
              </div>
              <div className="sa-entities-grid">
                {namedEntities.people?.length > 0 && (
                  <div className="sa-entity-group">
                    <span className="sa-entity-label">{mm ? "👤 People" : "👤 אנשים"}</span>
                    <div className="sa-tags">{namedEntities.people.map((p, i) => <span key={i} className="sa-tag">{p}</span>)}</div>
                  </div>
                )}
                {namedEntities.organizations?.length > 0 && (
                  <div className="sa-entity-group">
                    <span className="sa-entity-label">{mm ? "🏛️ Organizations" : "🏛️ ארגונים"}</span>
                    <div className="sa-tags">{namedEntities.organizations.map((o, i) => <span key={i} className="sa-tag">{o}</span>)}</div>
                  </div>
                )}
                {namedEntities.places?.length > 0 && (
                  <div className="sa-entity-group">
                    <span className="sa-entity-label">{mm ? "📍 Places" : "📍 מקומות"}</span>
                    <div className="sa-tags">{namedEntities.places.map((p, i) => <span key={i} className="sa-tag">{p}</span>)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {riskFlags.length > 0 && (
            <div className="sa-card sa-risk-card">
              <div className="sa-card-header">
                <Warning size={18} weight="duotone" />
                <span>{mm ? "Risk Flags" : "דגלים לתשומת לב"}</span>
              </div>
              {riskFlags.map((f, i) => (
                <div key={i} className="sa-risk-item">
                  <span className="sa-risk-flag">🚩 {f.flag}</span>
                  <span className="sa-risk-note">{f.note}</span>
                </div>
              ))}
            </div>
          )}

          <button className="sa-reset-btn" onClick={() => { setPhase("idle"); setData(null); }}>
            {t.newAnalysis}
          </button>

        </div>
      )}
    </div>
  );
}